import { Trans, t } from "@web-monorepo/i18n/macro";
import { DDSButton } from "@web-monorepo/dds";
import { clsx } from "clsx";
import FocusTrap from "focus-trap-react";
import type React from "react";
import type { AppMetadata, Tool, ToolPayload } from "./EditableSetup";
import EditHeader from "./EditHeader";
import styles from "./EditableToolEdit.module.css";
import {
  useEditableToolEdit,
  type UseEditableToolEditProps,
  type UseEditableToolEditResult,
} from "./useEditableToolEdit.vm";

type EditableToolEditProps = {
  renderEditItem: ({
    onEdit,
    payload,
    setFocusOnParent,
  }: {
    onEdit: (payload: ToolPayload, disabled?: boolean) => void;
    payload: ToolPayload;
    setFocusOnParent?: (focus: boolean) => void;
  }) => React.ReactElement;
  appMetadata: AppMetadata;
  closeEditScreen: VoidFunction;
  createTool: (payload: ToolPayload) => void;
  editTool: (toolId: string, payload: ToolPayload) => void;
  deleteTool: (toolId?: string) => void;
  showTool: (payload: ToolPayload) => void;
  tool: Tool | null;
  focusOnParent?: boolean;
  setFocusOnParent?: (focus: boolean) => void;
  /**
   * Dependency injection for the view model — Storybook stories override this
   * with a stub `useVm` returning canned state. Production callers leave the
   * default.
   */
  di?: {
    useVm?: (props: UseEditableToolEditProps) => UseEditableToolEditResult;
  };
};

export default function EditableToolEdit({
  renderEditItem,
  appMetadata,
  closeEditScreen,
  createTool,
  editTool,
  deleteTool,
  showTool,
  tool,
  focusOnParent,
  setFocusOnParent,
  di,
}: EditableToolEditProps) {
  const useVm = di?.useVm ?? useEditableToolEdit;
  const { payload, disabled, isEditingExisting, onEdit, startNow, saveForLater, deleteCurrent } = useVm({
    tool,
    createTool,
    editTool,
    deleteTool,
    showTool,
    closeEditScreen,
  });

  return (
    <FocusTrap
      active={focusOnParent}
      focusTrapOptions={{
        fallbackFocus: () => document.querySelector("[data-editable-tool-edit-container]") as HTMLElement,
        allowOutsideClick: true,
      }}
    >
      <div className={styles.container} data-editable-tool-edit-container tabIndex={-1}>
        <EditHeader title={tool ? t`Edit` : t`New`} close={closeEditScreen} />
        {renderEditItem({ onEdit, payload, setFocusOnParent })}

        <DDSButton
          data-name="editable_tool_edit:render:other_actions.start_now"
          disabled={disabled || !payload}
          onClick={startNow}
          className={clsx(styles.primaryButton, styles[appMetadata.variant])}
        >
          <Trans>Start now</Trans>
        </DDSButton>
        <DDSButton
          data-name="editable_tool_edit:render:other_actions.save_for_later"
          disabled={disabled}
          onClick={saveForLater}
          variant="secondary"
          className={styles.secondaryButton}
        >
          <Trans>Save for later</Trans>
        </DDSButton>
        {isEditingExisting && (
          <DDSButton
            data-name="editable_tool_edit:render:other_actions_delete"
            variant="destructiveTertiary"
            className={styles.deleteButton}
            onClick={deleteCurrent}
          >
            <Trans>Delete</Trans>
          </DDSButton>
        )}
      </div>
    </FocusTrap>
  );
}
